/**
 * SSML Readme 「よく使われる事例」の静的データ (正源)。
 *
 * ## なぜ事例を先に見せるか
 *
 * 従来の Readme は「強調 / 間 / 再生速度 / …」という **構文カテゴリ** が入口だった。
 * しかし利用者は「どのタグを使うか」ではなく **「こんな読み方にしたい」** から探す。
 * 構文から入る索引は、既にタグを知っている人にしか使えない。
 * そのため画面の初期表示は本ファイルの事例とし、構文カテゴリは
 * 「構文辞典」タブ (`categories.ts`) へ回す。
 *
 * ## 1 事例あたりの記載内容 (型は `types.ts` の `SsmlUseCase`)
 *
 *   - `problem`        … 課題 (現場で何が起きているか)
 *   - `countermeasure` … 対策 (どう直すか)
 *   - `examples`       … 利用 SSML (そのまま試せる形)
 *   - `expectedEffect` … 期待効果
 *   - `relatedSyntax`  … 関連構文 (構文辞典への参照)
 *   - `tags`           … タグ (絞り込みの軸。正源は `useCaseTags.ts`)
 *   - `confidence`     … 確度 (`established` / `trial`)
 *
 * ## `confidence: 'trial'` の扱い
 *
 * 読み・アクセントの制御 (`<phoneme>` など) は語や話者更新によって
 * 結果が変わり、**確実に改善されるとは言えない**。
 * そうした事例は `trial` とし、画面上も「改善を試みる方法」として表示する。
 * 「これを入れれば直る」と読める書き方はしない。
 *
 * ## 事例の増やし方 (現場ナレッジからの昇格)
 *
 * 現場で挙がった困りごとは、まず `knowledge.ts` の
 * `SSML_TUNING_KNOWLEDGE` へ **状態 (`status`) と最終確認日 (`verifiedAt`) 付き**で置く。
 * 対策と SSML が固まった (= `status: 'solved'` にできる) ものだけを
 * 本ファイルの事例へ昇格させる。
 *
 * ⚠️ 昇格してもナレッジ側は **消さない**。ナレッジの `relatedUseCaseId` で
 *   事例を指し、「何を確かめて事例になったか」の経緯を残す
 *   (経緯が消えると、効かなくなったときに再調査の起点が無くなる)。
 *
 * ## 将来の YAML 化
 *
 * 本ファイルは「定数配列 + 型注釈」だけで構成し、関数・条件分岐・
 * 他モジュール由来の値を **持たない**。よって配列部分をそのまま
 * YAML へ書き出せる (読み込み側だけを差し替えればよい)。
 * 絞り込みロジックは `useCaseFilter.ts` に分離しているため、
 * データ形式の変更が画面へ波及しない。
 */
import type { SsmlUseCase } from './types';

/**
 * よく使われる事例の一覧 (配列順が画面の表示順)。
 *
 * 並び順は「どれだけ多くの放送に効くか」を基準にしている
 * (駅名 → 方面 → 高齢者向け → お詫び → 数字 → 英字)。
 */
export const SSML_USE_CASES: readonly SsmlUseCase[] = [
  {
    id: 'station-name-clarity',
    title: '駅名を聞き取りやすくしたい',
    problem:
      '走行音のある車内では「次は◯◯です」の駅名が文の流れに埋もれ、利用者が降車駅を聞き取れず、乗務員に聞き返しが発生する。駅名は文中に 1 回しか出ないため、聞き逃すと情報が復旧しない。',
    countermeasure:
      '駅名の直前に短い間を置いて注意を切り替えさせ、駅名だけ話速を落として音量をわずかに上げる。Neural エンジンでは <emphasis> が効かないため、強調は <break> と <prosody>(rate / volume) の組み合わせで作る。',
    examples: [
      {
        label: '駅名の前に間を置き、駅名をゆっくり大きく読む',
        ssml: '<speak>次は<break time="300ms"/><prosody rate="90%" volume="+2dB">梅田</prosody>です。</speak>',
        note: 'まずこの形を基準にする。間は 200〜400ms、話速は 85〜95% の範囲で調整する。',
      },
      {
        label: '駅名を 2 回言う案内で 1 回目を際立たせる',
        ssml: '<speak>次は<prosody rate="88%" volume="+2dB">喜連瓜破</prosody>、<break time="400ms"/><prosody rate="88%">喜連瓜破</prosody>です。</speak>',
        note: '難読駅・長い駅名は繰り返すと確実性が上がる。2 回目は音量を上げず速度だけ落とす。',
      },
    ],
    expectedEffect:
      '駅名の前後に速度差と間が生まれ、走行音の中でも駅名が浮き上がって聞こえる。聞き返しの発生を減らせる。',
    relatedSyntax: [
      { category: 'emphasis', title: '駅名をゆっくり大きく読んで強調する' },
      { category: 'break', title: '文の区切りに短い間を入れる' },
      { category: 'volume', title: '重要な案内の音量を上げる' },
    ],
    tags: ['station-name', 'noisy', 'elderly'],
    confidence: 'established',
  },
  {
    id: 'direction-accent',
    title: '方面のアクセントを改善したい',
    problem:
      '「◯◯方面」の読みで、地名側のアクセントが平板になったり「方面」に不自然な高さが付いたりして、地名として認識されない。特に路線名と紛れる地名 (例: 「なかもず」) で起きやすい。',
    countermeasure:
      '読みそのものを指定できる <phoneme alphabet="x-amazon-pron-kana"> でアクセント記号付きのカナ読みを与え、狙った音の高さを試す。x-amazon-pron-kana ではカタカナに加えて「\'」(アクセント核) を書ける。併せて「方面」の直前に弱い間を入れると、地名と接尾語の境界が保たれやすい。',
    examples: [
      {
        label: 'カナ読みでアクセント核を指定して試す',
        ssml: '<speak><phoneme alphabet="x-amazon-pron-kana" ph="ナカ\'モズ">なかもず</phoneme>方面ゆき、普通電車です。</speak>',
        note: "アクセント核 (') の位置を 1 文字ずつずらして聴き比べる。話者・エンジン更新で結果が変わるため、採用前に必ず試聴する。",
      },
      {
        label: '地名と「方面」の間に弱い間を入れて境界を保つ',
        ssml: '<speak>この電車は<phoneme alphabet="x-amazon-pron-kana" ph="テンノ\'ージ">天王寺</phoneme><break strength="weak"/>方面ゆきです。</speak>',
        note: 'phoneme が効かない場合でも、間だけで地名の切れ目は改善しうる。',
      },
    ],
    expectedEffect:
      '地名のアクセントが狙いに近づき、方面が地名として聞き取れる可能性が高まる。⚠️ 確実に改善される方法ではない。ph の指定が無視される語もあるため、採用は必ず試聴結果で判断する (改善を試みる手段として使う)。',
    relatedSyntax: [
      { category: 'intonation', title: 'カナ読みでアクセントを指定して抑揚を試す' },
      { category: 'break', title: '間の強さを段階で指定する' },
      { category: 'emphasis', title: '固有名詞の読みをふりがなで指定する' },
    ],
    tags: ['direction', 'accent', 'station-name'],
    confidence: 'trial',
  },
  {
    id: 'elderly-friendly',
    title: '高齢者向けに聞き取りやすくしたい',
    problem:
      '標準の話速・抑揚では、高齢の利用者が情報量の多い案内 (乗り換え・行先・注意喚起) を追い切れず、内容が伝わらない。速度を落とすだけでは声が平板になり、かえって聞き流されることがある。',
    countermeasure:
      '全体の話速を 90% 前後に落とし、文と文の間に間を入れて1 文ずつ処理できるリズムにする。音量は +1〜2dB までにとどめ、上げすぎて音が硬くなる (聞き疲れする) のを避ける。',
    examples: [
      {
        label: '全体をゆっくりにし、文ごとに間を入れる',
        ssml: '<speak><prosody rate="90%" volume="+1dB">次は梅田です。<break time="400ms"/>お出口は右側です。<break time="400ms"/>足元にご注意ください。</prosody></speak>',
        note: '文ごとに 300〜500ms の間を置くのが目安。長すぎると放送尺が伸びるので実測する。',
      },
      {
        label: '定型の前置きは通常速度にして本文だけゆっくりにする',
        ssml: '<speak>お客様にお知らせいたします。<break time="500ms"/><prosody rate="88%">この電車は、次の梅田で回送となります。</prosody></speak>',
        note: '全体を遅くすると尺が伸びる。伝えたい本文に速度低下を集中させる。',
      },
    ],
    expectedEffect:
      '一語一語が明瞭になり、文単位で理解する余裕が生まれる。音量のみを上げた場合より聞き疲れしにくい。',
    relatedSyntax: [
      { category: 'rate', title: '全体をゆっくり読ませる' },
      { category: 'break', title: '文単位・段落単位で自動的に間を作る' },
      { category: 'volume', title: '音量と速度を合わせて聞き取りやすくする' },
    ],
    tags: ['elderly', 'noisy', 'phrasing'],
    confidence: 'established',
  },
  {
    id: 'delay-apology-tone',
    title: '遅延案内で申し訳ない感じを出したい',
    problem:
      '遅延やお詫びの案内が通常と同じ明るい調子で流れると、内容と声の印象が合わず「事務的」「軽い」と受け取られる。文面を丁寧にしても、抑揚が変わらないため誠意が伝わらない。',
    countermeasure:
      '話速をわずかに落とし (93〜95%)、音量も少し控えめ (-1dB 程度) にして落ち着いた調子にする。お詫びの前に長めの間を置いて、聞く姿勢が整ってから本文に入るようにする。⚠️ 音の高さ (pitch) は Neural エンジンで合成が失敗するため使わない。',
    examples: [
      {
        label: 'お詫びの前に間を置き、ゆっくりめの話速で読む',
        ssml: '<speak>お客様にお知らせいたします。<break time="700ms"/><prosody rate="95%">ただ今、この電車はおよそ5分の遅れで運転しております。ご迷惑をおかけし、申し訳ございません。</prosody></speak>',
        note: '落ち着きは「長めの間 + わずかに遅い話速」で作る。pitch は Neural エンジンで失敗するため指定しない。',
      },
      {
        label: 'お詫びの一文だけ抑えた調子にする',
        ssml: '<speak>まもなく発車いたします。<break time="400ms"/><prosody rate="93%" volume="-1dB">お待たせして申し訳ございません。</prosody></speak>',
        note: '全体を暗くせず、お詫びの一文にだけ適用すると案内全体の明瞭さを保てる。',
      },
    ],
    expectedEffect:
      '声の印象が内容と一致し、遅延の重さ・お詫びの意図が伝わる。「軽く流された」という不信感を避けられる。',
    relatedSyntax: [
      { category: 'intonation', title: '速度と音量をまとめて指定して印象を整える' },
      { category: 'break', title: '重要な案内の前で長めに間を取る' },
    ],
    tags: ['apology', 'number', 'phrasing'],
    confidence: 'trial',
  },
  {
    id: 'number-clarity',
    title: '数字を聞き取りやすくしたい',
    problem:
      '「3番線」「5分の遅れ」「17時30分」などの数値が、文中を流れる過程で聞き逃される。さらに数字は読み方が一定せず、「3」が「みっつ」と読まれる、時刻が数字の羅列に聞こえるなどの誤読も起きる。',
    countermeasure:
      'まず <say-as> で読み方を確定させ (cardinal / time / digits)、そのうえで数値部分だけ話速を落として音量をわずかに上げ、前後の文とコントラストを作る (音高 (pitch) は Neural エンジンで合成が失敗するため使わない)。読み方の確定と目立たせる処理は別の問題なので、両方を必ず組み合わせる。',
    examples: [
      {
        label: '番線を基数で読ませ、数値を目立たせる',
        ssml: '<speak>電車が到着します。<break time="300ms"/><prosody rate="88%" volume="+2dB"><say-as interpret-as="cardinal">3</say-as>番線</prosody>から発車します。</speak>',
      },
      {
        label: '時刻を時刻として読ませる',
        ssml: '<speak>次の発車は<prosody rate="90%"><say-as interpret-as="time">17:30</say-as></prosody>です。</speak>',
        note: 'say-as を付けないと「じゅうななコロンさんじゅう」のように読まれる場合がある。',
      },
      {
        label: '遅れ時分をゆっくり読ませる',
        ssml: '<speak>ただ今、<prosody rate="85%" volume="+2dB">およそ5分</prosody>の遅れで運転しています。</speak>',
      },
    ],
    expectedEffect:
      '数値の読みが安定し、かつ数値部分が分離して聞こえるため、「何番線か」「何分遅れか」を聞き返されにくくなる。',
    relatedSyntax: [
      { category: 'emphasis', title: '数字・記号の読み方を指定して誤読を防ぐ' },
      { category: 'emphasis', title: '遅延時分など数値を強調する' },
      { category: 'other', title: '日付・時刻の読み方を指定する' },
    ],
    tags: ['number', 'station-name', 'noisy'],
    confidence: 'trial',
  },
  {
    id: 'alphabet-abbreviation',
    title: '英字・略称を正しく読ませたい',
    problem:
      '「JR」「ICOCA」「OBA」のような英字・略称が、英単語として綴り読みされたり、意図しないローマ字読みになったりする。案内の中で会社名・路線名を誤って読むと、乗り換え先を誤解させる。',
    countermeasure:
      '1 文字ずつ読ませたいものは <say-as interpret-as="characters">、語として読ませたいものは <sub alias="…"> でカナ読みを与える。どちらが自然かは語によって異なるため、両方を作って聴き比べる。',
    examples: [
      {
        label: '1 文字ずつ読ませる (頭字語)',
        ssml: '<speak><say-as interpret-as="characters">JR</say-as>線はお乗り換えです。</speak>',
        note: 'アルファベットを 1 文字ずつ読む。「ジェイアール」に近い読みを狙う。',
      },
      {
        label: 'カナ読みを直接与える (語として読ませる)',
        ssml: '<speak><sub alias="イコカ">ICOCA</sub>もご利用いただけます。</speak>',
        note: '綴り読みされたくない固有名詞は sub が確実。表記と読みを分離できる。',
      },
      {
        label: '略称を正式名称で読ませる',
        ssml: '<speak><sub alias="大阪シティバス">OBA</sub>との乗り換えは、駅係員にお尋ねください。</speak>',
      },
    ],
    expectedEffect:
      '英字・略称が意図した読みで発話され、社名・路線名の誤解を避けられる。表記は原文のまま残せるため、画面上の文言と音声を別々に管理できる。',
    relatedSyntax: [
      { category: 'emphasis', title: '固有名詞の読みをふりがなで指定する' },
      { category: 'emphasis', title: '数字・記号の読み方を指定して誤読を防ぐ' },
      { category: 'other', title: '記号や特殊文字をエスケープする' },
    ],
    tags: ['alphabet', 'line-name', 'phrasing'],
    confidence: 'established',
  },
];

/**
 * 事例 ID から 1 件取得する。
 *
 * 未知の ID では `undefined` を返すため、呼び出し側でフォールバックできる。
 */
export function findSsmlUseCase(id: string): SsmlUseCase | undefined {
  return SSML_USE_CASES.find((useCase) => useCase.id === id);
}
